import { ReactElement, use, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckRounded, Close, NotificationsActiveOutlined } from '@mui/icons-material';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import { Button, IconButton } from '@mui/material';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';
import { isUniversalDevice } from 'shared/models/device-type';
import DeviceDetailsProviderContext from 'shared/providers/device-details/contexts/device-details-provider-context';
import DevicesProviderContext from 'shared/providers/devices/contexts/devices-provider-context';

import { useDeviceDetailsHook } from 'features/device/device-details/hooks/device-details-hook';
import { useSendTestEventContextProvider } from 'features/device/send-test-event/context/send-test-event-provider';
import SendTestEventPopup from 'features/device/send-test-event/views/send-test-event-popup.component';
import DeviceDeletePopup from 'features/device/views/device-delete-popups';
import { GatewayType } from 'features/gateway/domain/models/gateway-type';

import 'features/device/device-details/views/device-details-popup-header.component.scss';

type Props = {
  onCloseCallback?: () => void;
};

const DeviceDetailsPopupHeader = ({ onCloseCallback }: Props): ReactElement => {
  const { t } = useTranslation('deviceDetails');
  const { openSendTestEventPopup } = useSendTestEventContextProvider();
  const { handleClosePopup, handleCancelEdit, handleSetEditMode, currentViewingMode, isLoading, currentDevice } = useDeviceDetailsHook();

  const { allowUnlinking } = use(DevicesProviderContext);
  const { isSendTestEventPopupOpen } = useSendTestEventContextProvider();
  const { linkedOrganisationErrorMessage } = use(DeviceDetailsProviderContext);

  const { hasPermission } = usePermissionAuthorization();

  const isLinkedToSystem = currentDevice?.system;
  const isRoomDevice = currentDevice?.externalId?.split('_').pop() === 'room';

  const isSendTestEventButtonVisible = useMemo(
    () =>
      currentDevice?.organisationUnitId &&
      (currentDevice?.gateway?.type === GatewayType.IoTEdge || currentDevice?.gateway?.type === GatewayType.IoTEdgeCluster) &&
      !isLinkedToSystem &&
      isUniversalDevice(currentDevice?.type),
    [currentDevice, isLinkedToSystem],
  );

  const isCreating = currentViewingMode === 'creation';
  const isEditing = currentViewingMode === 'editing';

  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState<boolean>(false);

  return (
    <div className="device-details-header-container">
      {(isCreating || isEditing) && (
        <Button
          data-testid="deviceDetailCancelButton"
          variant="text"
          onClick={
            isCreating
              ? (): void => {
                  onCloseCallback?.();
                  handleClosePopup();
                }
              : handleCancelEdit
          }
          disabled={isLoading}
        >
          {t('cancelFormButton')}
        </Button>
      )}
      {(currentViewingMode === 'creation' || currentViewingMode === 'editing') && (
        <Button
          data-testid="deviceDetailSubmitButton"
          startIcon={<CheckRounded />}
          variant="contained"
          type="submit"
          loading={isLoading}
          loadingPosition="start"
          disabled={!!linkedOrganisationErrorMessage}
        >
          {t('saveFormButton')}
        </Button>
      )}
      {currentViewingMode === 'viewing' && (
        <>
          {isSendTestEventButtonVisible && hasPermission(Permission.SendTestEvent) && (
            <Button
              data-testid="deviceSendTestEventButton"
              startIcon={<NotificationsActiveOutlined className="icon-on-text" />}
              variant="text"
              onClick={() => {
                if (currentDevice) openSendTestEventPopup(currentDevice);
              }}
              disabled={isLoading}
            >
              {t('sendTestEventButton')}
            </Button>
          )}
          {allowUnlinking && !isRoomDevice && hasPermission(Permission.DeleteDevice) && (
            <Button
              data-testid="deviceDetailRemoveButton"
              startIcon={<DeleteForeverOutlinedIcon className="icon-on-text" />}
              variant="text"
              onClick={() => setIsDeletePopupOpen(true)}
              disabled={isLoading}
            >
              {t('removeFormButton')}
            </Button>
          )}
          {hasPermission(Permission.UpdateDevice) && (
            <Button
              data-testid="deviceDetailEditButton"
              startIcon={<ModeEditOutlineOutlinedIcon className="icon-on-text" />}
              variant="text"
              onClick={() => handleSetEditMode()}
              disabled={isLoading}
            >
              {t('editFormButton')}
            </Button>
          )}
        </>
      )}

      <IconButton
        aria-label="close"
        className="close-button"
        onClick={() => {
          onCloseCallback?.();
          handleClosePopup();
        }}
        data-testid="devicePopupCloseButton"
      >
        <Close />
      </IconButton>

      {isSendTestEventPopupOpen && <SendTestEventPopup />}
      {isDeletePopupOpen && (
        <DeviceDeletePopup
          device={currentDevice}
          handleOnClose={() => {
            onCloseCallback?.();
            setIsDeletePopupOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default DeviceDetailsPopupHeader;
